import { HttpsUrl } from "@redotech/util/url";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { ProductSelectionType } from "./email-builder";
import { Provider } from "./order";

export const cartContextSchema = z.object({
  existingCartId: z.string().nullable(),
  productIdsForRecommendations: z.array(z.string()),
  alreadyInCart: z.array(
    z.object({
      productId: z.string(),
      variantId: z.string(),
      quantity: z.number().optional(),
    }),
  ),
});
export type CartContext = z.infer<typeof cartContextSchema>;

const getEmailRecommendedProductsBaseSchema = z.object({
  blockId: z.string().nullable(), // used to disambiguate between identical parallel requests from the email builder
  teamId: z.string(),
  customerId: z.string().nullable(),
  provider: createEnum(Provider, { enumName: "Provider" }),
  cartContext: cartContextSchema.nullable(),
  generateGmailAnnotationImages: z.boolean(),
});

const getEmailRecommendedStaticVariantsManualSchema =
  getEmailRecommendedProductsBaseSchema.extend({
    productSelectionType: z.literal(ProductSelectionType.MANUAL),
    manuallySelectedProducts: z.array(
      z.object({ productId: z.string(), variantId: z.string() }),
    ),
  });

const getEmailRecommendedInteractiveProductsManualSchema =
  getEmailRecommendedProductsBaseSchema.extend({
    productSelectionType: z.literal(ProductSelectionType.MANUAL),
    manuallySelectedProducts: z.array(z.object({ productId: z.string() })),
  });

const getEmailRecommendedProductsVariantsDynamicSchema =
  getEmailRecommendedProductsBaseSchema.extend({
    productSelectionType: z.literal(ProductSelectionType.DYNAMIC),
    numberOfProducts: z.number().nullable(),
    productFilterId: z.string().nullable(),
  });

export const getEmailRecommendedStaticVariantsSchema = z.union([
  getEmailRecommendedStaticVariantsManualSchema,
  getEmailRecommendedProductsVariantsDynamicSchema,
]);
export type GetEmailRecommendedStaticVariantsParams = z.infer<
  typeof getEmailRecommendedStaticVariantsSchema
>;

export const getEmailRecommendedInteractiveProductsSchema = z.union([
  getEmailRecommendedInteractiveProductsManualSchema,
  getEmailRecommendedProductsVariantsDynamicSchema,
]);
export type GetEmailRecommendedInteractiveProductsParams = z.infer<
  typeof getEmailRecommendedInteractiveProductsSchema
>;

export const emailRecommendedVariantSchema = z.object({
  title: z.string(),
  variantTitle: z.string(),
  priceSet: z.object({
    presentment_money: z.object({
      amount: z.number().or(z.string()),
      currency_code: z.string(),
    }),
  }),
  image: z.object({ src: z.string().optional() }),
  quantity: z.number(),
  handle: z.string().optional(),
  compareAtPrice: z.string().nullish(),
  pdpUrl: z.string(),
  alreadyInCart: z.boolean(),
  productId: z.string(),
  variantId: z.string(),
  provider: createEnum(Provider, { enumName: "Provider" }),
});

/**
 * Emails do not use the standardized RedoRecommendedProduct intertface because emails are different enough that they deserve their own interface.
 *
 * For example, emails recommend a *variant*, not a *product*, and have to track things like `alreadyInCart` which is only relevant in emails.
 */
export type EmailRecommendedVariant = z.infer<
  typeof emailRecommendedVariantSchema
>;

const httpsUrlSchema = z
  .url()
  .refine((url): url is HttpsUrl => url.startsWith("https://"), {
    message: "Must be an HTTPS URL",
  })
  .transform((url) => url.toString());

export const gmailAnnotatedEmailRecommendedVariantSchema =
  emailRecommendedVariantSchema.extend({
    croppedImageUrl: httpsUrlSchema.nullable(),
  });
export type GmailAnnotatedEmailRecommendedVariant = z.infer<
  typeof gmailAnnotatedEmailRecommendedVariantSchema
>;

export const interactiveProductVariantSchema = z.object({
  id: z.string(),
  title: z.string(),
  formattedPrice: z.string(),
  price: z.object({
    amount: z.number().or(z.string()),
    currencyCode: z.string(),
  }),
  image: z.string(),
  selectedOptions: z.array(z.object({ name: z.string(), value: z.string() })),
});
export type InteractiveProductVariant = z.infer<
  typeof interactiveProductVariantSchema
>;

export const variantDimensionSchema = z.object({
  name: z.string(),
  options: z.array(z.string()),
  selectedOption: z.string().optional(), // TODO: use this to support abandonment emails (things already in cart at send time)
});
export type VariantDimension = z.infer<typeof variantDimensionSchema>;

/**
 * The initial configuration of a product for an interactive (AMP or Apple) email.
 *
 * Typically this will consist entirely of product recommendations, but it should also eventually support existing cart items.
 */
export const interactiveCartProductSchema = z.object({
  id: z.string(),
  title: z.string(),
  variantDimensions: z.array(variantDimensionSchema),
  variants: z.array(interactiveProductVariantSchema),
  primaryImageUrl: z.string(),
  pdpUrl: z.string().optional(),
  handle: z.string().optional(),
  // TODO: quantity?: number for abandoned cart emails
});
export type InteractiveCartProduct = z.infer<
  typeof interactiveCartProductSchema
>;

export function convertToVariantDimensions(
  variants: InteractiveProductVariant[],
  // TODO: selected options
): VariantDimension[] {
  const variantDimensionsMap: { [key: string]: Set<string> } = {};

  for (const variant of variants) {
    for (const option of variant.selectedOptions) {
      if (!variantDimensionsMap[option.name]) {
        variantDimensionsMap[option.name] = new Set();
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      variantDimensionsMap[option.name].add(option.value);
    }
  }

  return Object.keys(variantDimensionsMap)
    .filter((name) =>
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      Array.from(variantDimensionsMap[name]).some(
        (name) => name.toLowerCase() !== "default title",
      ),
    )
    .map((name) => ({
      name,
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      options: Array.from(variantDimensionsMap[name]),
    }));
}
